import { c as _c } from "react/compiler-runtime";
import React, { useRef, useState } from "react";
import { RiAddLine } from "react-icons/ri";
import { APISearchSelect, APISearchSelectAction, APISearchSelectContent, APISearchSelectTrigger } from "@/components/ui";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import { CREATE_PATIENT_MAX_MATCHES, patientPrefillFromSearch } from "@/lib/patient-search-prefill";
import { PERMISSIONS, hasAnyPermission } from "@/lib/permissions";
import { clickableText } from "@/lib/tailwind-utils";
import { PatientCreate, PatientSummary } from "@/types/patient";
import CreatePatientModal from "./create-patient-modal";
import PatientDisplay from "./patient-display";
import { patientSearchQuery } from "@/api/provider/user/patient/patient-search-query";
interface PatientSearchProps {
  patient?: PatientSummary;
  onPatientSelect: (patient: PatientSummary | undefined) => void;
  placeholder?: string;
  className?: string;
  clearAfterSelect?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  showPhoneNumber?: boolean;
  showDateOfBirth?: boolean;
  searchFilter?: (patients: PatientSummary[]) => PatientSummary[];
  showCreatePatient?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  /** Optional content rendered at the end of each result row (e.g. a status badge). */
  renderOptionExtra?: (patient: PatientSummary) => React.ReactNode;
  /** Accessible name for the search input when no associated label is available. */
  ariaLabel?: string;
  /** Stable prefix for e2e/proof selectors. */
  testIdPrefix?: string;
}
const PatientSearch: React.FC<PatientSearchProps> = t0 => {
  const $ = _c(56);
  const {
    patient,
    onPatientSelect,
    placeholder: t1,
    className: t2,
    clearAfterSelect: t3,
    autoFocus: t4,
    disabled: t5,
    showPhoneNumber: t6,
    showDateOfBirth: t7,
    searchFilter,
    showCreatePatient: t8,
    hasError,
    errorMessage,
    renderOptionExtra,
    ariaLabel,
    testIdPrefix: t9
  } = t0;
  const placeholder = t1 === undefined ? "Search patient by phone, id, name or dob" : t1;
  const className = t2 === undefined ? "" : t2;
  const clearAfterSelect = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const disabled = t5 === undefined ? false : t5;
  const showPhoneNumber = t6 === undefined ? true : t6;
  const showDateOfBirth = t7 === undefined ? true : t7;
  const showCreatePatient = t8 === undefined ? true : t8;
  const testIdPrefix = t9 === undefined ? "patient-search" : t9;
  const {
    currentUser
  } = useUser();
  const lastSearchTermRef = useRef("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState();
  let t10;
  if ($[0] !== currentUser) {
    t10 = hasAnyPermission(currentUser, [PERMISSIONS.PATIENTS_CREATE, PERMISSIONS.PATIENTS_PROFILES_WRITE]);
    $[0] = currentUser;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const canCreatePatient = t10;
  let t11;
  if ($[2] !== renderOptionExtra || $[3] !== showDateOfBirth || $[4] !== showPhoneNumber) {
    t11 = patient_0 => {
      const display = <PatientDisplay patient={patient_0} showPhoneNumber={showPhoneNumber} showLocation={true} showDateOfBirth={showDateOfBirth} showPronouns={true} layout="horizontal" useLinks={false} className={renderOptionExtra ? "min-w-0 flex-1" : "w-full"} />;
      return {
        id: patient_0.id.toString(),
        name: `${patient_0.firstName} ${patient_0.lastName}`,
        optionNode: renderOptionExtra ? <div className="flex w-full items-center justify-between gap-2">{display}<div className="shrink-0">{renderOptionExtra(patient_0)}</div></div> : display
      };
    };
    $[2] = renderOptionExtra;
    $[3] = showDateOfBirth;
    $[4] = showPhoneNumber;
    $[5] = t11;
  } else {
    t11 = $[5];
  }
  const formatPatientOption = t11;
  let t12;
  if ($[6] !== searchFilter) {
    t12 = async searchTerm => {
      const results = await queryClient.fetchQuery(patientSearchQuery.list(searchTerm));
      if (results && searchFilter) {
        return searchFilter(results);
      }
      return results;
    };
    $[6] = searchFilter;
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  const handleSearch = t12;
  let t13;
  if ($[8] !== clearAfterSelect || $[9] !== onPatientSelect) {
    t13 = selectedPatient => {
      onPatientSelect(selectedPatient);
      if (selectedPatient) {
        lastSearchTermRef.current = "";
      }
      if (clearAfterSelect && selectedPatient) {
        setTimeout(() => onPatientSelect(undefined), 0);
      }
    };
    $[8] = clearAfterSelect;
    $[9] = onPatientSelect;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  const handlePatientSelect = t13;
  let t14;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = open => {
      if (open) {
        setCreatePrefill(patientPrefillFromSearch(lastSearchTermRef.current));
      }
      setIsCreateOpen(open);
    };
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  const handleCreateOpenChange = t14;
  let t15;
  if ($[12] !== onPatientSelect) {
    t15 = newPatient => {
      lastSearchTermRef.current = "";
      onPatientSelect(newPatient);
    };
    $[12] = onPatientSelect;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const handleCreatePatient = t15;
  const t16 = `flex w-full flex-col ${className}`;
  const t17 = `${testIdPrefix}-input`;
  let t18;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = value => {
      lastSearchTermRef.current = value;
    };
    $[14] = t18;
  } else {
    t18 = $[14];
  }
  let t19;
  if ($[15] !== ariaLabel || $[16] !== autoFocus || $[17] !== disabled || $[18] !== errorMessage || $[19] !== hasError || $[20] !== placeholder || $[21] !== t17) {
    t19 = <APISearchSelectTrigger placeholder={placeholder} autoFocus={autoFocus} disabled={disabled} className="w-full" hasError={hasError} errorMessage={errorMessage} ariaLabel={ariaLabel} dataTestId={t17} onInputChange={t18} />;
    $[15] = ariaLabel;
    $[16] = autoFocus;
    $[17] = disabled;
    $[18] = errorMessage;
    $[19] = hasError;
    $[20] = placeholder;
    $[21] = t17;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== canCreatePatient || $[24] !== showCreatePatient || $[25] !== testIdPrefix) {
    t20 = showCreatePatient && canCreatePatient ? <button type="button" className="flex w-full items-center gap-2 text-left text-primary hover:underline" onClick={() => handleCreateOpenChange(true)} data-testid={`${testIdPrefix}-create-patient`}><RiAddLine className="size-4 shrink-0" aria-hidden={true} />Create a new patient</button> : "No patients found";
    $[23] = canCreatePatient;
    $[24] = showCreatePatient;
    $[25] = testIdPrefix;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== canCreatePatient || $[28] !== showCreatePatient || $[29] !== testIdPrefix) {
    t21 = t22 => {
      const {
        optionCount,
        close
      } = t22;
      return showCreatePatient && canCreatePatient && optionCount <= CREATE_PATIENT_MAX_MATCHES ? <APISearchSelectAction className="gap-2 text-primary" onSelect={() => {
        close();
        handleCreateOpenChange(true);
      }} data-testid={`${testIdPrefix}-create-patient`}><RiAddLine className="size-4 shrink-0" aria-hidden={true} />Create a new patient</APISearchSelectAction> : null;
    };
    $[27] = canCreatePatient;
    $[28] = showCreatePatient;
    $[29] = testIdPrefix;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const t22 = `${testIdPrefix}-results`;
  const t23 = `${testIdPrefix}-option`;
  let t24;
  if ($[31] !== t20 || $[32] !== t21 || $[33] !== t22 || $[34] !== t23) {
    t24 = <APISearchSelectContent emptyMessage={t20} trailingRow={t21} dataTestId={t22} optionTestIdPrefix={t23} />;
    $[31] = t20;
    $[32] = t21;
    $[33] = t22;
    $[34] = t23;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== clearAfterSelect || $[37] !== formatPatientOption || $[38] !== handlePatientSelect || $[39] !== handleSearch || $[40] !== patient || $[41] !== t19 || $[42] !== t24) {
    t25 = <div className="flex w-full flex-col md:flex-row md:space-x-4"><div className="flex w-full justify-start"><APISearchSelect value={patient} onValueChange={handlePatientSelect} onSearch={handleSearch} formatOption={formatPatientOption} clearAfterSelect={clearAfterSelect}>{t19}{t24}</APISearchSelect></div></div>;
    $[36] = clearAfterSelect;
    $[37] = formatPatientOption;
    $[38] = handlePatientSelect;
    $[39] = handleSearch;
    $[40] = patient;
    $[41] = t19;
    $[42] = t24;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== canCreatePatient || $[45] !== createPrefill || $[46] !== handleCreatePatient || $[47] !== isCreateOpen || $[48] !== showCreatePatient) {
    t26 = showCreatePatient && canCreatePatient && <CreatePatientModal onPatientCreate={handleCreatePatient} open={isCreateOpen} onOpenChange={handleCreateOpenChange} initialData={createPrefill}><span className={clickableText}>Create a new Patient</span></CreatePatientModal>;
    $[44] = canCreatePatient;
    $[45] = createPrefill;
    $[46] = handleCreatePatient;
    $[47] = isCreateOpen;
    $[48] = showCreatePatient;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t26) {
    t27 = <div className="pt-1">{t26}</div>;
    $[50] = t26;
    $[51] = t27;
  } else {
    t27 = $[51];
  }
  let t28;
  if ($[52] !== t16 || $[53] !== t25 || $[54] !== t27) {
    t28 = <><div className={t16}>{t25}{t27}</div></>;
    $[52] = t16;
    $[53] = t25;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  return t28;
};
export default PatientSearch;